// Installs the global client-error reporting (window errors, unhandled rejections, chunk preload
// failures) → POST /api/client-errors, listed in Stjórnun → Villuskráning.
import './errorReporter';
import { lazy, Suspense, useEffect, useReducer, useState } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router';
import { useAuth } from './auth/AuthProvider';
import { Wordmark } from './components/brand';
import ChatListScreen from './chat/ChatListScreen';
import ChatScreen from './chat/ChatScreen';
import { AppShell } from './components/layout';
import { AppErrorBoundary, reportRouteError, RouteError } from './components/RouteError';
import { useToast } from './components/toast';
import { Button, CenteredSpinner, EmptyState } from './components/ui';
import { useT } from './i18n';
import { OfflineRuntime } from './offline/OfflineRuntime';
import { onUpdateAvailable } from './offline/registerSW';
import ChoirScreen from './screens/ChoirScreen';
import CollectionScreen from './screens/CollectionScreen';
import EventRosterScreen from './screens/EventRosterScreen';
import EventScreen from './screens/EventScreen';
import HomeScreen from './screens/HomeScreen';
import MemberScreen from './screens/MemberScreen';
import InstallScreen from './screens/InstallScreen';
import NotFound from './screens/NotFound';
import PrivacyScreen from './screens/PrivacyScreen';
import SettingsScreen from './screens/SettingsScreen';
import SongScreen from './screens/SongScreen';
import SongsScreen from './screens/SongsScreen';
import AcceptTokenScreen from './screens/auth/AcceptTokenScreen';
import LoginScreen from './screens/auth/LoginScreen';
import OnboardingScreen from './screens/auth/OnboardingScreen';
import SignupScreen from './screens/auth/SignupScreen';
import { parseLinkHash, type LinkHash } from './lib/linkHash';
import { PublicPrivacyScreen } from './screens/privacy/PrivacyLink';
import { WifiOff } from 'lucide-react';

// Admin screens are lazy so pdf.js and admin code never load on member devices. Their chunks are not
// precached either: opening Stjórnun offline (or after a deploy, before "Ný útgáfa" is accepted) can
// fail to load them — RouteError explains that and reloads (React.lazy keeps the rejection).
const AdminScreen = lazy(() => import('./screens/admin/AdminScreen'));

const router = createBrowserRouter([
  {
    element: <AppShell />,
    // Only when the shell itself fails: nothing around it to keep.
    errorElement: <RouteError fullScreen />,
    children: [
      {
        // Errors in any screen render inside the shell's <main>, so the navigation stays usable; the
        // boundary resets on the next navigation.
        errorElement: <RouteError />,
        children: [
          { path: '/', element: <HomeScreen /> },
          { path: '/events/:eventId', element: <EventScreen /> },
          { path: '/events/:eventId/roster', element: <EventRosterScreen /> },
          { path: '/songs', element: <SongsScreen /> },
          { path: '/chat', element: <ChatListScreen /> },
          { path: '/chat/:chatId', element: <ChatScreen /> },
          { path: '/collections/:collectionId', element: <CollectionScreen /> },
          { path: '/choir', element: <ChoirScreen /> },
          { path: '/choir/:userId', element: <MemberScreen /> },
          { path: '/settings', element: <SettingsScreen /> },
          { path: '/install', element: <InstallScreen /> },
          { path: '/privacy', element: <PrivacyScreen /> },
          {
            path: '/admin/*',
            element: (
              <Suspense fallback={<CenteredSpinner />}>
                <AdminScreen />
              </Suspense>
            ),
          },
          { path: '*', element: <NotFound /> },
        ],
      },
    ],
  },
  // Full-screen song page (sheet viewer) lives outside the shell chrome.
  { path: '/songs/:songId', element: <SongScreen />, errorElement: <RouteError fullScreen /> },
]);

/** One-time links carry the token in the URL fragment: /#/virkja/<token>, /#/skraning/<token> (lib/linkHash.ts). */
function readHashLink(): LinkHash | null {
  return parseLinkHash(window.location.hash);
}

function Splash() {
  return (
    <div className="flex h-dvh flex-col items-center justify-center gap-6">
      <Wordmark />
      <CenteredSpinner />
    </div>
  );
}

/**
 * "Ný útgáfa – endurhlaða" while a new version waits. Subscribed once; the toast is derived from the
 * waiting update, so a language change replaces it (in the new language) instead of adding another.
 */
function UpdateToast() {
  const toast = useToast();
  const t = useT();
  const [activate, setActivate] = useState<(() => Promise<void>) | null>(null);
  useEffect(() => onUpdateAvailable((fn) => setActivate(() => fn)), []);
  useEffect(() => {
    if (!activate) return;
    const id = toast.show(t('common.update_available'), {
      sticky: true,
      action: { label: t('common.reload'), onClick: () => void activate() },
    });
    return () => toast.dismiss(id);
  }, [activate, t, toast]);
  return null;
}

export default function App() {
  const auth = useAuth();
  const t = useT();
  const [link, setLink] = useState<LinkHash | null>(readHashLink);
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const inApp = auth.status === 'signed-in' && !auth.me.mustOnboard;

  useEffect(() => {
    const onHash = () => setLink(readHashLink());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Outside the app the router is not rendered, but it still owns the URL (and keeps it in sync on
  // Back/Forward): follow its location for /privacy below.
  useEffect(() => (inApp ? undefined : router.subscribe(() => rerender())), [inApp]);

  // The privacy policy must be readable before signing in (Act 90/2018): /privacy opened while signed
  // out or onboarding shows it full-screen instead of the sign-in screen (the routed page needs the shell).
  const publicPrivacy = !inApp && router.state.location.pathname === '/privacy';
  // Through the router, so it opens at "/" after signing in.
  const closePublicPrivacy = () => void router.navigate('/', { replace: true });

  let content;
  if (link?.kind === 'signup') content = <SignupScreen key={link.token} token={link.token} />;
  else if (link) content = <AcceptTokenScreen key={link.token} token={link.token} />;
  else if (auth.status === 'loading') content = <Splash />;
  else if (publicPrivacy) content = <PublicPrivacyScreen onClose={closePublicPrivacy} />;
  else if (auth.status === 'signed-out') content = <LoginScreen />;
  else if (auth.status === 'offline-no-session')
    content = (
      <div className="flex h-dvh items-center justify-center">
        <EmptyState
          icon={WifiOff}
          title={t('common.offline')}
          action={<Button onClick={() => window.location.reload()}>{t('common.retry')}</Button>}
        >
          {t('error.network')}
        </EmptyState>
      </div>
    );
  else if (auth.me.mustOnboard) content = <OnboardingScreen />;
  else content = <RouterProvider router={router} onError={reportRouteError} />;

  return (
    <>
      {/* Background part: a crash here must not take the screen down (silent, like OfflineRuntime). */}
      <AppErrorBoundary silent>
        <UpdateToast />
      </AppErrorBoundary>
      {auth.status === 'signed-in' && !auth.me.mustOnboard ? (
        <AppErrorBoundary key={auth.me.id} silent>
          <OfflineRuntime me={auth.me} />
        </AppErrorBoundary>
      ) : null}
      {/* The router has its own boundaries; this one catches the screens outside it. */}
      <AppErrorBoundary>{content}</AppErrorBoundary>
    </>
  );
}
